import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Zap, ArrowLeft } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <header className="border-b border-white/10 bg-void-paper/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-neon-mint rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">TrafficFlow <span className="text-neon-mint">AI</span></span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-8">Terms of Service</h1>
        <p className="text-gray-400 mb-8">Last updated: January 24, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8 text-gray-300">
          <section>
            <h2 className="font-heading text-xl text-white mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing or using TrafficFlow AI ("Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">2. Description of Service</h2>
            <p>
              TrafficFlow AI is a credit-based traffic exchange platform that allows users to earn credits by viewing promotional content and spend credits to promote their own links. Additional features include AI-powered content generation, landing page creation, and lead management.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">3. Account Registration</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You must be at least 18 years old to create an account</li>
              <li>You must provide accurate and complete registration information</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You are responsible for all activities under your account</li>
              <li>One account per person; multiple accounts are prohibited</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">4. Subscription Plans and Billing</h2>
            <h3 className="font-semibold text-white mt-4 mb-2">4.1 Plans</h3>
            <p>We offer Free, Pro ($19/month), and Power ($39/month) subscription plans. Yearly plans are available at a discounted rate.</p>
            
            <h3 className="font-semibold text-white mt-4 mb-2">4.2 Billing</h3>
            <p>Paid subscriptions are billed in advance on a monthly or yearly basis. All fees are non-refundable except as expressly stated in these Terms.</p>
            
            <h3 className="font-semibold text-white mt-4 mb-2">4.3 Cancellation Policy</h3>
            <p className="bg-neon-amber/10 border border-neon-amber/30 rounded-lg p-4 mt-2">
              <strong className="text-neon-amber">Important:</strong> You may cancel your subscription at any time. However, cancellation requests must be submitted at least <strong>3 days before your next renewal date</strong> to avoid being charged for the following billing period. Cancellations submitted less than 3 days before renewal will take effect in the subsequent billing cycle.
            </p>
            
            <h3 className="font-semibold text-white mt-4 mb-2">4.4 Refunds</h3>
            <p>Refund requests are considered on a case-by-case basis within 7 days of purchase. Credits that have been used or features that have been accessed may affect refund eligibility. Contact support@trafficflowai.com for refund inquiries.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">5. Credits</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Credits are virtual currency used within the platform</li>
              <li>Credits have no cash value and cannot be exchanged for money</li>
              <li>Credits are non-transferable between accounts</li>
              <li>Unused credits do not expire while your account remains active</li>
              <li>Credits may be forfeited if your account is terminated for violation of these Terms</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">6. Acceptable Use</h2>
            <p>You agree NOT to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Promote illegal activities or content</li>
              <li>Distribute malware, viruses, or harmful code</li>
              <li>Engage in fraud, phishing, or deceptive practices</li>
              <li>Promote adult content, gambling, or controlled substances</li>
              <li>Harass, abuse, or harm others</li>
              <li>Infringe on intellectual property rights</li>
              <li>Use automated systems (bots) to game the credit system</li>
              <li>Create multiple accounts to abuse promotions or referral bonuses</li>
              <li>Circumvent any access restrictions or security measures</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">7. Content Guidelines</h2>
            <p>All promotions and landing pages must comply with our content guidelines:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Content must be legal in all jurisdictions where it is displayed</li>
              <li>No misleading or false advertising</li>
              <li>No hate speech, discrimination, or offensive content</li>
              <li>Respect intellectual property rights</li>
              <li>Promotions must accurately represent the destination</li>
            </ul>
            <p className="mt-2">We reserve the right to remove content that violates these guidelines without notice.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">8. AI-Generated Content</h2>
            <p>
              Our AI features generate content suggestions based on your inputs. You are responsible for reviewing and editing AI-generated content before use. We do not guarantee the accuracy, originality, or appropriateness of AI-generated content.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">9. Intellectual Property</h2>
            <p>
              The Service, including its design, features, and content, is owned by TrafficFlow AI and protected by intellectual property laws. You retain ownership of content you create but grant us a license to display it within the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRAFFICFLOW AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">11. Termination</h2>
            <p>
              We may terminate or suspend your account at any time for violations of these Terms. Upon termination, your right to use the Service ceases immediately. Sections that should survive termination will remain in effect.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email or platform notification. Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">13. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable laws. Any disputes arising from these Terms shall be resolved through binding arbitration.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">14. Contact Us</h2>
            <p>
              For questions about these Terms, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> <a href="mailto:support@trafficflowai.com" className="text-neon-mint hover:underline">support@trafficflowai.com</a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-12 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition-colors text-neon-mint">Terms of Service</Link>
          <Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
          <Link to="/support" className="hover:text-white transition-colors">Support</Link>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
